/**
 * Profiles repository.
 *
 * The `profiles` table holds therapist + assistant accounts (one row per
 * authenticated user). The auth context already loads the current
 * therapist's profile; this module exposes additional reads + writes
 * (e.g. dashboard layout updates, listing teammates).
 *
 * See `src/data/README.md` for the rules of the repository layer.
 */

import { supabase } from '@/integrations/supabase/client';
import { unwrap, unwrapMaybe, toSupabaseError } from '@/lib/supabase-helpers';
import type {
  Tables,
  TablesUpdate,
  Json,
} from '@/integrations/supabase/types';

export type Profile = Tables<'profiles'>;
export type ProfileUpdate = TablesUpdate<'profiles'>;

/** Get the profile row for a given auth user. */
export async function getProfileByUserId(userId: string): Promise<Profile | null> {
  return unwrapMaybe(
    supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle(),
  );
}

/** List every profile in an organisation (e.g. for the therapist picker). */
export async function listOrganizationProfiles(orgId: string): Promise<Profile[]> {
  return unwrap(
    supabase
      .from('profiles')
      .select('*')
      .eq('organization_id', orgId)
      .order('last_name', { ascending: true }),
  );
}

/**
 * Persist a custom dashboard layout for a therapist. The layout is a
 * JSON blob; we keep the type loose because the schema lives client-side
 * (see `lib/dashboardLayout.ts`).
 *
 * No `.select().single()` readback — the RLS SELECT was known to return
 * `PGRST116` when `auth.uid()` was briefly out of sync with the client
 * cache, which used to surface as the "Speichern fehlgeschlagen" toast.
 * The caller refreshes its cached profile optimistically via
 * `AuthContext.updateLocalProfile` and keeps a localStorage copy (see
 * `lib/dashboardLayoutStorage.ts`) so it does not need the updated row
 * back from Postgres.
 *
 * Throws a `toSupabaseError`-wrapped error on failure — the caller is
 * expected to log the details and fall back to the localStorage copy.
 */
export async function saveDashboardLayout(
  userId: string,
  layout: Json,
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ dashboard_layout: layout })
    .eq('user_id', userId);
  if (error) throw toSupabaseError(error);
}

/**
 * Persist a custom Topbar-tab order for a therapist (Bookmark-Leisten-
 * Muster, see `src/features/navTabOrder.ts`). Same shape/rationale as
 * {@link saveDashboardLayout} — no readback, caller updates the local
 * profile optimistically via `AuthContext.updateLocalProfile`.
 */
export async function saveNavTabOrder(
  userId: string,
  order: string[],
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ nav_tab_order: order })
    .eq('user_id', userId);
  if (error) throw toSupabaseError(error);
}

/** Update an arbitrary subset of profile fields. */
export async function updateProfile(
  userId: string,
  patch: ProfileUpdate,
): Promise<Profile> {
  return unwrap(
    supabase
      .from('profiles')
      .update(patch)
      .eq('user_id', userId)
      .select('*')
      .single(),
  );
}
